import { Grid } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Autocomplete from "../../../../components/ui/Autocomplete";
import { useIntl } from "../../../../contexts/IntlContext";

export type EstablishmentOption = { id: string; name: string };
export type CameraOption = {
  deviceId: number;
  deviceName: string;
  establishmentId: string;
  establishmentName?: string | null;
  channelUuid?: string;
  channelName?: string;
};

type Props = {
  establishments: EstablishmentOption[];
  selectedEstablishmentId: string;
  onEstablishmentChange: (establishmentId: string) => void;
  cameraOptions: CameraOption[];
  selectedDeviceId: number | null;
  onDeviceChange: (device: CameraOption | null) => void;
  loading?: boolean;
};

/**
 * Câmera/quadra: par de Autocomplete (estabelecimento → câmera), extraído do fluxo original de
 * criação de Live (`LiveStreams/index.tsx`) para ser reaproveitado também pelo fluxo de período,
 * sem duplicar a lógica de seleção.
 */
export default function DeviceSelector({
  establishments,
  selectedEstablishmentId,
  onEstablishmentChange,
  cameraOptions,
  selectedDeviceId,
  onDeviceChange,
  loading = false,
}: Props) {
  const intl = useIntl();
  const theme = useTheme();
  const selectedEstablishment =
    establishments.find((e) => e.id === selectedEstablishmentId) ?? null;
  const selectedCamera =
    cameraOptions.find((c) => c.deviceId === selectedDeviceId) ?? null;

  return (
    <>
      <Grid item xs={12} md={6}>
        <Autocomplete
          options={establishments}
          value={selectedEstablishment}
          onChange={(_, next) => onEstablishmentChange(next?.id ?? "")}
          disabled={loading}
          getOptionLabel={(opt) => opt.name}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          label={intl.formatMessage({ id: "dashboard.establishmentLabel" })}
          slotProps={{ popper: { sx: { zIndex: theme.zIndex.modal + 20 } } }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Autocomplete
          options={cameraOptions}
          value={selectedCamera}
          onChange={(_, next) => onDeviceChange(next ?? null)}
          disabled={loading || !selectedEstablishment}
          loading={loading}
          getOptionLabel={(opt) => {
            const place = selectedEstablishment?.name ?? opt.establishmentName ?? "";
            return place ? `${place} / ${opt.deviceName}` : opt.deviceName;
          }}
          isOptionEqualToValue={(a, b) => a.deviceId === b.deviceId}
          label={intl.formatMessage({ id: "analyticsV2.court" })}
          helperText={
            !loading && selectedEstablishmentId && !cameraOptions.length
              ? intl.formatMessage({ id: "liveStreams.noCourts" })
              : undefined
          }
          slotProps={{ popper: { sx: { zIndex: theme.zIndex.modal + 20 } } }}
        />
      </Grid>
    </>
  );
}
